import React, {
  useState,
  useEffect,
  useContext,
} from "react";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import {
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Alertas from "./Alertas";
import ActualizarFamiliaDb from "../firebase/ActualizarFamiliaDb";
import useObtenerFamilia from "../hooks/useObtenerFamilia";

import { UserContext } from "../context/UserContext";

import {
  Container,
  Segment,
  Form,
  Button,
  Header,
  Icon,
  Message,
} from "semantic-ui-react";

const ActualizaFamilia = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const [familia] = useObtenerFamilia(id);

  const [
    nuevoCampo,
    setNuevoCampo,
  ] = useState("");

  const [
    familiaOriginal,
    setFamiliaOriginal,
  ] = useState("");

  const [
    alerta,
    cambiarAlerta,
  ] = useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    verificandoUso,
    setVerificandoUso,
  ] = useState(false);

  const [
    familiaBloqueada,
    setFamiliaBloqueada,
  ] = useState(false);

  // ==========================================================
  // VOLVER
  // ==========================================================

  const volver = () => {
    navigate(-1);
  };

  // ==========================================================
  // VERIFICAR SI LA FAMILIA YA ESTÁ SIENDO UTILIZADA
  // ==========================================================

  const verificarFamiliaEnUso =
    async (nombreFamilia) => {
      if (!users?.emp_id) {
        throw new Error(
          "No se encontró la empresa del usuario"
        );
      }

      const nombreNormalizado =
        String(
          nombreFamilia || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!nombreNormalizado) {
        return false;
      }

      // ======================================================
      // 1. HOMECARE
      // Primera propagación: equipos
      // ======================================================

      const qEquipos =
        query(
          collection(
            db,
            "equipos"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "familia",
            "==",
            nombreNormalizado
          ),

          limit(1)
        );

      const snapEquipos =
        await getDocs(
          qEquipos
        );

      if (
        !snapEquipos.empty
      ) {
        return true;
      }

      // ======================================================
      // 2. SERVICIO TÉCNICO
      // Primera propagación: ingresostcab
      // ======================================================

      const qIngresos =
        query(
          collection(
            db,
            "ingresostcab"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "familia",
            "==",
            nombreNormalizado
          ),

          limit(1)
        );

      const snapIngresos =
        await getDocs(
          qIngresos
        );

      if (
        !snapIngresos.empty
      ) {
        return true;
      }

      return false;
    };

  // ==========================================================
  // VERIFICAR DUPLICADO
  // ==========================================================

  const verificarNombreDuplicado =
    async (nombreFamilia) => {
      if (!users?.emp_id) {
        return false;
      }

      const nombreNormalizado =
        String(
          nombreFamilia || ""
        )
          .toLocaleUpperCase()
          .trim();

      const qFamilias =
        query(
          collection(
            db,
            "familias"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "familia",
            "==",
            nombreNormalizado
          )
        );

      const snap =
        await getDocs(
          qFamilias
        );

      return snap.docs.some(
        (doc) =>
          doc.id !== id
      );
    };

  // ==========================================================
  // GUARDAR
  // ==========================================================

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      cambiarEstadoAlerta(
        false
      );

      cambiarAlerta({});

      const nombreNuevo =
        String(
          nuevoCampo || ""
        )
          .toLocaleUpperCase()
          .trim();

      // ======================================================
      // VACÍO
      // ======================================================

      if (!nombreNuevo) {
        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "El nombre de la familia no puede estar vacío.",
        });

        return;
      }

      const originalNormalizado =
        String(
          familiaOriginal || ""
        )
          .toLocaleUpperCase()
          .trim();

      // ======================================================
      // SIN CAMBIOS
      // ======================================================

      if (
        nombreNuevo ===
        originalNormalizado
      ) {
        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No se han realizado cambios en la familia.",
        });

        return;
      }

      setIsSaving(true);

      try {
        // ====================================================
        // REVALIDAR ANTES DE GUARDAR
        //
        // Se revisa SIEMPRE el nombre original.
        // ====================================================

        const enUso =
          await verificarFamiliaEnUso(
            familiaOriginal
          );

        if (enUso) {
          setFamiliaBloqueada(
            true
          );

          cambiarEstadoAlerta(
            true
          );

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Esta familia ya está siendo utilizada en el sistema y no puede modificarse.",
          });

          return;
        }

        // ====================================================
        // EVITAR DUPLICADO
        // ====================================================

        const duplicado =
          await verificarNombreDuplicado(
            nombreNuevo
          );

        if (duplicado) {
          cambiarEstadoAlerta(
            true
          );

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Ya existe una familia con este nombre.",
          });

          return;
        }

        // ====================================================
        // ACTUALIZAR
        // ====================================================

        await ActualizarFamiliaDb({
          id,

          familia:
            nombreNuevo,

          userMod:
            user?.email || "",

          fechaMod:
            new Date(),
        });

        setFamiliaOriginal(
          nombreNuevo
        );

        setNuevoCampo(
          nombreNuevo
        );

        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "exito",
          mensaje:
            "Familia actualizada correctamente.",
        });
      } catch (error) {
        console.error(
          "Error actualizando familia:",
          error
        );

        cambiarEstadoAlerta(
          true
        );

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No fue posible actualizar la familia.",
        });
      } finally {
        setIsSaving(false);
      }
    };

  // ==========================================================
  // CARGAR FAMILIA Y VALIDAR USO
  // ==========================================================

  useEffect(() => {
    if (
      !familia ||
      !users?.emp_id
    ) {
      return;
    }

    const nombreFamilia =
      String(
        familia.familia || ""
      )
        .toLocaleUpperCase()
        .trim();

    setNuevoCampo(
      nombreFamilia
    );

    setFamiliaOriginal(
      nombreFamilia
    );

    const comprobarUso =
      async () => {
        setVerificandoUso(
          true
        );

        try {
          const enUso =
            await verificarFamiliaEnUso(
              nombreFamilia
            );

          setFamiliaBloqueada(
            enUso
          );
        } catch (error) {
          console.error(
            "Error verificando familia:",
            error
          );

          /*
           * Si no podemos comprobarlo,
           * bloqueamos por seguridad.
           */
          setFamiliaBloqueada(
            true
          );

          cambiarEstadoAlerta(
            true
          );

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "No fue posible verificar si la familia puede modificarse.",
          });
        } finally {
          setVerificandoUso(
            false
          );
        }
      };

    comprobarUso();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    familia,
    users?.emp_id,
  ]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth: 1400,
        padding:
          "24px 28px",
        background:
          "#f8fafc",
        minHeight:
          "100vh",
      }}
    >
      {/* ==================================================== */}
      {/* ENCABEZADO */}
      {/* ==================================================== */}

      <div
        style={{
          display: "flex",
          alignItems:
            "center",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            minWidth: 48,
            borderRadius: 12,
            background:
              "#e5f7f7",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
          }}
        >
          <Icon
            name="edit outline"
            style={{
              margin: 0,
              color:
                "#0d9da5",
              fontSize: 21,
            }}
          />
        </div>

        <div>
          <Header
            as="h2"
            style={{
              margin: 0,
              color:
                "#172b4d",
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            Actualizar Familia
          </Header>

          <div
            style={{
              color:
                "#8793a5",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            Modifica los datos de la familia de equipamiento
          </div>
        </div>
      </div>

      {/* ==================================================== */}
      {/* FORMULARIO */}
      {/* ==================================================== */}

      <Segment
        style={{
          borderRadius: 8,
          border:
            "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          padding: 0,
          background:
            "#ffffff",
          overflow:
            "hidden",
        }}
      >
        {/* CABECERA */}

        <div
          style={{
            padding:
              "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display: "flex",
            alignItems:
              "center",
            gap: 9,
            color:
              "#253858",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Icon
            name="tag"
            style={{
              color:
                "#0d9da5",
            }}
          />

          Datos de la familia
        </div>

        <div
          style={{
            padding: 22,
          }}
        >
          {/* ================================================ */}
          {/* VERIFICANDO */}
          {/* ================================================ */}

          {verificandoUso && (
            <Message
              icon
              size="small"
              style={{
                marginTop: 0,
              }}
            >
              <Icon
                name="circle notched"
                loading
              />

              <Message.Content>
                <Message.Header>
                  Verificando familia
                </Message.Header>

                Estamos comprobando si la familia puede ser modificada.
              </Message.Content>
            </Message>
          )}

          {/* ================================================ */}
          {/* BLOQUEADA */}
          {/* ================================================ */}

          {!verificandoUso &&
            familiaBloqueada && (
              <Message
                warning
                icon
                style={{
                  marginTop: 0,
                }}
              >
                <Icon name="lock" />

                <Message.Content>
                  <Message.Header>
                    Familia protegida
                  </Message.Header>

                  Esta familia ya está siendo utilizada en el sistema y no puede modificarse.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* DISPONIBLE */}
          {/* ================================================ */}

          {!verificandoUso &&
            !familiaBloqueada && (
              <Message
                positive
                icon
                size="small"
                style={{
                  marginTop: 0,
                }}
              >
                <Icon name="unlock" />

                <Message.Content>
                  <Message.Header>
                    Familia disponible para edición
                  </Message.Header>

                  Esta familia todavía no ha sido utilizada en el sistema.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* FORMULARIO */}
          {/* ================================================ */}

          <Form
            size="large"
            onSubmit={
              handleSubmit
            }
          >
            <Form.Field>
              <label
                style={{
                  color:
                    "#344563",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 7,
                  display: "block",
                }}
              >
                Familia
              </label>

              <Form.Input
                fluid
                type="text"
                name="familia"
                icon="tag"
                iconPosition="left"
                placeholder="Ingrese Familia Equipamiento Médico"
                value={
                  nuevoCampo
                }
                disabled={
                  isSaving ||
                  verificandoUso ||
                  familiaBloqueada
                }
                onChange={(e) =>
                  setNuevoCampo(
                    e.target.value
                  )
                }
              />
            </Form.Field>

            {/* ============================================== */}
            {/* BOTONES */}
            {/* ============================================== */}

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                alignItems:
                  "center",
                gap: 10,
                marginTop: 30,
                paddingTop: 18,
                borderTop:
                  "1px solid #edf0f4",
              }}
            >
              <Button
                type="button"
                onClick={volver}
                disabled={
                  isSaving
                }
                style={{
                  minWidth: 110,
                  background:
                    "#ffffff",
                  color:
                    "#526273",
                  border:
                    "1px solid #d7dee7",
                  borderRadius: 6,
                  boxShadow:
                    "none",
                }}
              >
                <Icon name="arrow left" />
                Volver
              </Button>

              {!familiaBloqueada && (
                <Button
                  type="submit"
                  loading={
                    isSaving
                  }
                  disabled={
                    isSaving ||
                    verificandoUso
                  }
                  style={{
                    minWidth: 125,
                    background:
                      "#0d9da5",
                    color:
                      "#ffffff",
                    border:
                      "1px solid #0d9da5",
                    borderRadius: 6,
                    boxShadow:
                      "none",
                  }}
                >
                  {!isSaving && (
                    <Icon name="save" />
                  )}

                  Actualizar
                </Button>
              )}
            </div>
          </Form>
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* ALERTAS */}
      {/* ==================================================== */}

      <Alertas
        tipo={
          alerta.tipo
        }
        mensaje={
          alerta.mensaje
        }
        estadoAlerta={
          estadoAlerta
        }
        cambiarEstadoAlerta={
          cambiarEstadoAlerta
        }
      />
    </Container>
  );
};

export default ActualizaFamilia;